import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { AssessmentService } from "../../../../../_services/company/assessmentService";
import { sweetAlert } from "../../../../../utils/UniversalFunction";

/**
 * Supplier-admin modal: pick which received assessments a specific team
 * member should be able to act on. Loads the full assignment roster + this
 * user's current delegation set, diffs on save into POST (add) and DELETE
 * (revoke) per-assignment calls.
 */
export default function UserAssessmentDelegationModal({
  show,
  user,
  onClose,
  onSaved,
}) {
  const [assignments, setAssignments] = useState([]);
  const [existing, setExisting] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !user?.userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [assignRes, delRes] = await Promise.all([
        AssessmentService.listMyAssignments({ limit: 200 }),
        AssessmentService.listUserDelegations(user.userId),
      ]);
      if (cancelled) return;
      setLoading(false);
      const items = assignRes?.data?.data?.items ?? [];
      const ids = new Set(
        (delRes?.data?.data?.assignment_ids ?? []).map((v) => Number(v))
      );
      setAssignments(items);
      setExisting(ids);
      setSelected(new Set(ids));
    })();
    return () => {
      cancelled = true;
    };
  }, [show, user?.userId]);

  const toggle = (assignmentId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assignmentId)) next.delete(assignmentId);
      else next.add(assignmentId);
      return next;
    });
  };

  const diff = useMemo(() => {
    const toAdd = [];
    const toRemove = [];
    selected.forEach((id) => {
      if (!existing.has(id)) toAdd.push(id);
    });
    existing.forEach((id) => {
      if (!selected.has(id)) toRemove.push(id);
    });
    return { toAdd, toRemove };
  }, [selected, existing]);

  const handleSave = async () => {
    if (!user?.userId) return;
    setSaving(true);
    for (const assignmentId of diff.toAdd) {
      const r = await AssessmentService.addAssignmentDelegates(assignmentId, [
        user.userId,
      ]);
      if (!r?.isSuccess) {
        setSaving(false);
        return;
      }
    }
    for (const assignmentId of diff.toRemove) {
      const r = await AssessmentService.removeAssignmentDelegate(
        assignmentId,
        user.userId
      );
      if (!r?.isSuccess) {
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    sweetAlert("success", "Assessment assigned successfully.");
    onSaved?.();
    onClose?.();
  };

  const displayName = user?.name || user?.email || "";

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onClose}
      centered
      size="lg"
      scrollable
    >
      <Modal.Header
        closeButton={!saving}
        style={{ borderBottom: "1px solid var(--rule)" }}
      >
        <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
          Manage assessments — {displayName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#f8fafc" }}>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          Check the assessments this team member should be able to open, fill,
          and submit. Unchecked assignments stay admin-only.
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
            Loading assessments…
          </div>
        ) : assignments.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#64748b",
              fontSize: 13,
            }}
          >
            No assessments have been received yet.
          </div>
        ) : (
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--rule)",
              borderRadius: 8,
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {assignments.map((a) => {
              const isSelected = selected.has(Number(a.id));
              return (
                <label
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(Number(a.id))}
                    disabled={saving}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>
                      {a.assessment_title || `Assessment #${a.assessment_id}`}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      From {a.company_name || `Company #${a.company_id}`} · Assigned{" "}
                      {a.assigned_at?.slice(0, 10)}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontFamily: "var(--font-mono)",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: statusBg(a.status_key),
                      color: statusFg(a.status_key),
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {a.status_key}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid var(--rule)", gap: 8 }}>
        <div style={{ fontSize: 12, color: "#64748b", marginRight: "auto" }}>
          {diff.toAdd.length} to add · {diff.toRemove.length} to revoke
        </div>
        <button
          type="button"
          className="kpi-btn-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        {saving ? (
          <button type="button" className="kpi-btn-primary" disabled>
            <Spinner animation="border" size="sm" className="me-1" />
            Saving…
          </button>
        ) : (
          <button
            type="button"
            className="kpi-btn-primary"
            onClick={handleSave}
            disabled={
              loading || (diff.toAdd.length === 0 && diff.toRemove.length === 0)
            }
          >
            Save
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

const statusBg = (key) => {
  switch (key) {
    case "SUBMITTED":
    case "REVIEWED":
      return "#dcfce7";
    case "IN_PROGRESS":
      return "#fef9c3";
    case "CANCELLED":
      return "#fee2e2";
    default:
      return "#e0f2fe";
  }
};

const statusFg = (key) => {
  switch (key) {
    case "SUBMITTED":
    case "REVIEWED":
      return "#15803d";
    case "IN_PROGRESS":
      return "#a16207";
    case "CANCELLED":
      return "#b91c1c";
    default:
      return "#075985";
  }
};
